import React, { Suspense, useState } from 'react';
import { Card, Divider, Typography, Breadcrumb, Skeleton, Alert, notification, Button, Checkbox, Spin } from 'antd';
import { CheckCircleOutlined, FileOutlined, DownOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom-v5-compat';
import { useMutation } from '@tanstack/react-query';
import Title from 'antd/lib/typography/Title';
import { ReadmeIcon } from '~/components/assets/ReadmeIcon';
import { useSession } from '~/hooks/useSession';
import { useLifecycle } from '~/hooks/useLifecycle';
import { Flex } from '~/components/Flex';
import { FormButtons } from '~/components/form/FormButtons';
import { useMutationInvalidator } from '~/hooks/useMutationInvalidator';
import { partnerManagementService } from '~/services';
import { useScrollToAccept } from '~/hooks/useScrollToAccept';
import { useLegalAgreementAccepted } from '~/hooks/useLegalAgreementAccepted';
import { getCarrierEulaConfig, isKnownAgreementType } from '~/config/carrierEulaConfig';

// Lazy load PdfViewer to avoid react-pdf breaking useQuery in code-split chunks.
const PdfViewer = React.lazy(() => import('~/components/PdfViewer/PdfViewer'));

const { Text } = Typography;

/**
 * Returns the returnUrl only when it is a safe, app-relative path (a single leading "/").
 * Rejects absolute and protocol-relative values (e.g. "//evil.com", "https://…", "/\\evil") so a
 * crafted `returnUrl` query param cannot be used for an open redirect / phishing.
 */
export const toSafeReturnUrl = (value: string | null): string | null =>
  value && /^\/(?![/\\])/.test(value) ? value : null;

/**
 * Generic, config-driven carrier brand + EULA acceptance page used during customer registration.
 * Displays the carrier logo, the verbatim service-mark disclaimer, and the full EULA inline with a
 * scroll-then-accept clickwrap, then persists acceptance (who/when/version) via Partner Management.
 */
const CarrierEulaAgreement = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { legalAgreementType } = useParams();
  const { sessionPartnerUrl, sessionPartnerId } = useSession();
  const [eulaCheckbox, setEulaCheckbox] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const returnUrl = toSafeReturnUrl(searchParams.get('returnUrl'));

  const validType = isKnownAgreementType(legalAgreementType);
  const config = validType ? getCarrierEulaConfig(legalAgreementType) : null;

  // Pass undefined for an unrecognized type so the hook skips the lookup entirely (no dead GET).
  const { accepted, isLoading, isError } = useLegalAgreementAccepted(validType ? legalAgreementType : undefined);

  const changeStateMutation = useMutation({
    mutationFn: useMutationInvalidator(partnerManagementService.legalAgreements.changeState),
    onSuccess: () => {
      setEulaCheckbox(false);
      notification.success({
        message: 'Agreement Accepted',
        description: 'You have successfully accepted the agreement.',
      });
      if (returnUrl) {
        navigate(returnUrl);
      }
    },
    onError: (err) => {
      setError('Failed to save your acceptance. Please try again.');
      console.error('Failed to save legal agreement acceptance:', err);
    },
  });

  const { hasScrolledToBottom, bottomMarkerRef } = useScrollToAccept({ alreadyAccepted: accepted });

  useLifecycle({
    onMount: () => {
      document.title = config ? config.title : 'Agreement';
    },
  });

  if (!validType || !config) {
    return (
      <Card>
        <Alert
          type="error"
          showIcon
          message="Unknown agreement"
          description="The requested agreement type is not recognized."
        />
      </Card>
    );
  }

  if (config.disabled) {
    return (
      <Card>
        <Alert
          type="warning"
          showIcon
          message="Agreement not yet available"
          description="This agreement is not available yet. Please check back once the required documents have been published."
        />
      </Card>
    );
  }

  // Surface a failed acceptance lookup rather than silently rendering the clickwrap (which would
  // look like the partner never accepted and risk a duplicate acceptance record).
  if (isError) {
    return (
      <Card>
        <Alert
          type="error"
          showIcon
          message="Unable to load agreement status"
          description="We couldn't determine whether this agreement has already been accepted. Please refresh and try again."
        />
      </Card>
    );
  }

  const handleSubmit = () => {
    changeStateMutation.mutate({
      partnerId: sessionPartnerId,
      payload: {
        legalAgreementType: config.legalAgreementType,
        accepted: true,
        eulaVersion: config.eulaVersion,
      },
    });
  };

  const handleCancel = () => {
    navigate(returnUrl ?? `/${sessionPartnerUrl}`);
  };

  return (
    <Card>
      <Flex direction="vertical">
        <Flex direction="vertical" gap="none">
          <Flex direction="horizontal" justifyContent="space-between">
            <Breadcrumb separator=">">
              <Breadcrumb.Item>
                <a href={`/${sessionPartnerUrl}`}>
                  <Text>Home</Text>
                </a>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <Text strong>{config.title}</Text>
              </Breadcrumb.Item>
            </Breadcrumb>
            {config.docsUrl && (
              <a rel="noreferrer" target="_blank" href={config.docsUrl}>
                <ReadmeIcon style={{ fontSize: '20px' }} />
              </a>
            )}
          </Flex>
          <Divider />
        </Flex>

        <Flex direction="vertical" alignItems="center">
          <Skeleton loading={isLoading}>
            <Flex direction="vertical" gap="md" maxWidth={800}>
              <Flex direction="horizontal" justifyContent="space-between" alignItems="center">
                <Flex direction="horizontal" gap="md" alignItems="center">
                  <img src={config.logoSrc} alt={config.logoAlt} width={40} height={40} />
                  <Title style={{ margin: 0 }} level={3}>
                    {config.title}
                  </Title>
                </Flex>
                {accepted && (
                  <span>
                    <CheckCircleOutlined /> Accepted
                  </span>
                )}
              </Flex>

              <Alert type="info" showIcon message={config.description} />

              {!accepted ? (
                <Text>
                  By clicking the "I accept" button, you accept and agree to be bound by all of the provisions of this
                  Agreement. You represent that you have the authority to bind your company, if done on behalf of a
                  company. If you do not agree, do not click "I accept."
                </Text>
              ) : (
                <Text>You have already accepted this agreement.</Text>
              )}

              {!accepted && (
                <Card style={{ backgroundColor: '#fafafa', border: '1px solid #d9d9d9' }}>
                  <Flex direction="vertical" gap="sm">
                    <Flex direction="horizontal" justifyContent="space-between" alignItems="center">
                      <Text strong>Full License Agreement</Text>
                      {!hasScrolledToBottom && (
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          <DownOutlined /> Scroll to bottom to enable acceptance
                        </Text>
                      )}
                    </Flex>
                    <div
                      style={{
                        maxHeight: '500px',
                        overflow: 'auto',
                        overflowX: 'hidden',
                        backgroundColor: '#fff',
                        border: '1px solid #e8e8e8',
                        padding: '16px',
                      }}
                    >
                      <Suspense
                        fallback={
                          <Flex direction="vertical" alignItems="center" justifyContent="center">
                            <Spin size="large" />
                            <Text type="secondary" style={{ marginTop: '16px' }}>
                              Loading PDF viewer...
                            </Text>
                          </Flex>
                        }
                      >
                        <PdfViewer file={config.eulaPdfPath} bottomMarkerRef={bottomMarkerRef} />
                      </Suspense>
                    </div>
                  </Flex>
                </Card>
              )}

              <Flex direction="horizontal" gap="md" justifyContent="flex-end" alignItems="center">
                <Button type="link" href={config.eulaPdfPath} target="_blank">
                  <FileOutlined /> Open in New Tab
                </Button>
                {!accepted && (
                  <Checkbox
                    disabled={!hasScrolledToBottom}
                    checked={eulaCheckbox}
                    onChange={(e) => setEulaCheckbox(e.target.checked)}
                  >
                    {config.acceptanceLabel}
                  </Checkbox>
                )}
              </Flex>

              {error && <Alert type="error" showIcon message={error} closable onClose={() => setError(null)} />}

              {!accepted && (
                <FormButtons
                  onBack={handleCancel}
                  onSubmit={handleSubmit}
                  onSubmitLabel="I Accept"
                  onBackLabel="Cancel"
                  touched={eulaCheckbox}
                  isLoading={changeStateMutation.isPending}
                />
              )}
            </Flex>
          </Skeleton>
          <Divider />
          <Flex direction="horizontal" justifyContent="center">
            <Text type="secondary">{config.disclaimerText}</Text>
          </Flex>
        </Flex>
      </Flex>
    </Card>
  );
};

export default CarrierEulaAgreement;
